import React, { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import CommandPalette from '../CommandPalette/CommandPalette';
import { useCommandPaletteShortcut } from '../CommandPalette/useCommandPaletteShortcut';
import NotificationsPanel from '../Notifications/NotificationsPanel';
import AISidebar from '../AISidebar/AISidebar';
import { safeStorage } from '../../utils/safeStorage';
import { useOfflineSyncSession } from '../../offlinesync';

const THEME_KEY = 'tunifact-theme';
const COLLAPSED_KEY = 'tunifact-sidebar-collapsed';

export function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return safeStorage.getItem(COLLAPSED_KEY) === 'true';
  });
  const [isDark, setIsDark] = useState<boolean>(() => {
    return safeStorage.getItem(THEME_KEY) === 'dark';
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifsOpen, setNotifsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Keep the offline cache warm for the signed-in company (no-op until configured via env).
  useOfflineSyncSession();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    safeStorage.setItem(THEME_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    safeStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteShortcut(openPalette);

  return (
    <div className="gf-app">
      <div className={`app-shell ${collapsed ? 'collapsed' : ''}`}>
        <Sidebar
          isOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
          collapsed={collapsed}
        />

        <div className="main-area">
          <Header
            onMenuClick={() => setMobileOpen(true)}
            onToggleSidebar={() => {
              if (window.innerWidth < 1024) {
                setMobileOpen((o) => !o);
              } else {
                setCollapsed((c) => !c);
              }
            }}
            onOpenSearch={openPalette}
            onToggleNotifs={() => setNotifsOpen((o) => !o)}
            onToggleAI={() => setAiOpen((o) => !o)}
            aiOpen={aiOpen}
            onToggleTheme={() => setIsDark((d) => !d)}
            isDark={isDark}
          />

          <main className="content thin-scroll">
            <div className="page">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenAI={() => setAiOpen(true)}
      />
      <NotificationsPanel open={notifsOpen} onClose={() => setNotifsOpen(false)} />
      <AISidebar open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}

export default Layout;
